import {
  buildCatalogFilterOptions,
  matchesCatalogFilters,
  matchesProductQuery,
  productSearchText,
} from "./catalog-rules.js";
import { formatFitmentLabel } from "./fitment";
import type { CartLine, CustomerProfile, Product, ProductCategory } from "../types/catalog";

export type CreatedProductResponse = {
  id: string;
  name?: string;
  sku?: string;
  brand?: string;
  description?: string;
  category?: string;
  condition?: string;
  fitment?: string;
  fitmentMake?: string;
  fitmentModel?: string;
  fitmentYearFrom?: number | null;
  fitmentYearTo?: number | null;
  fitmentEngine?: string;
  fitmentNotes?: string;
  priceKes?: number;
  stockQty?: number;
  isPromoted?: boolean;
  imageStorageKey?: string;
  imageAlt?: string;
};

export type ApiProduct = Omit<Product, "imageAlt" | "isPromoted"> & {
  imageAlt?: string;
  imageStorageKey?: string;
  isPromoted?: boolean | number;
};

export type ProductsResponse = {
  products: ApiProduct[];
};

export type ApiCategory = {
  id: string;
  name: string;
  slug?: string;
  imageStorageKey?: string;
};

export type CategoriesResponse = {
  categories: ApiCategory[];
};

export type CategoryResponse = {
  category: ApiCategory | null;
};

export type CatalogFilters = {
  brand: string;
  condition: string;
  engine: string;
  make: string;
  model: string;
  year: string;
};

export type CatalogFilterOptions = {
  brands: string[];
  conditions: string[];
  engines: string[];
  makes: string[];
  models: string[];
  years: string[];
};

export type CreatedOrderResponse = {
  orderId: string;
  orderCode?: string;
  customerAccessToken?: string;
  totalKes: number;
};

export type CartResponse = {
  customerProfile: CustomerProfile;
  items: CartLine[];
};

export function isCreatedProductResponse(value: unknown): value is CreatedProductResponse {
  return typeof value === "object" && value !== null && "id" in value && typeof value.id === "string";
}

export function isCreatedOrderResponse(value: unknown): value is CreatedOrderResponse {
  return (
    typeof value === "object" &&
    value !== null &&
    "orderId" in value &&
    typeof value.orderId === "string" &&
    "totalKes" in value &&
    typeof value.totalKes === "number"
  );
}

export function productFromApi(product: ApiProduct): Product {
  const fitmentYearFromValue = Number(product.fitmentYearFrom);
  const fitmentYearToValue = Number(product.fitmentYearTo);
  const fitment = product.fitment?.trim() || formatFitmentLabel({
    fitmentMake: product.fitmentMake?.trim() ?? "",
    fitmentModel: product.fitmentModel?.trim() ?? "",
    fitmentYearFrom: Number.isInteger(fitmentYearFromValue)
      ? fitmentYearFromValue
      : null,
    fitmentYearTo: Number.isInteger(fitmentYearToValue)
      ? fitmentYearToValue
      : null,
    fitmentEngine: product.fitmentEngine?.trim() ?? "",
  }) || "General fitment";

  return {
    ...product,
    description: product.description?.trim() ?? "",
    condition: product.condition?.trim() || "new",
    fitment,
    fitmentMake: product.fitmentMake?.trim() ?? "",
    fitmentModel: product.fitmentModel?.trim() ?? "",
    fitmentYearFrom: Number.isInteger(fitmentYearFromValue)
      ? fitmentYearFromValue
      : null,
    fitmentYearTo: Number.isInteger(fitmentYearToValue)
      ? fitmentYearToValue
      : null,
    fitmentEngine: product.fitmentEngine?.trim() ?? "",
    fitmentNotes: product.fitmentNotes?.trim() ?? "",
    imageStorageKey: product.imageStorageKey?.trim() ?? "",
    imageAlt: product.imageAlt?.trim() || `${product.name} product image`,
    isPromoted: Boolean(product.isPromoted),
  };
}

export function categoryFromApi(category: ApiCategory): ProductCategory {
  return {
    id: category.id,
    name: category.name,
    imageStorageKey: category.imageStorageKey?.trim() ?? "",
  };
}

export function stockLabel(product: Product, isAdmin: boolean) {
  if (isAdmin) {
    return `${product.stockQty} in stock`;
  }

  if (product.stockQty <= 0) {
    return "Out of stock";
  }

  if (product.stockQty <= 3) {
    return "Few units left";
  }

  return "";
}

export {
  buildCatalogFilterOptions,
  formatFitmentLabel,
  matchesCatalogFilters,
  matchesProductQuery,
  productSearchText,
};
